import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { 
  Save, 
  Globe, 
  ShoppingCart, 
  Star, 
  Instagram, 
  Facebook,
  AlertCircle,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '@/context/auth';
import { hasOrgAdminAccess } from '@/context/auth/types';
import { supabase } from '@/integrations/supabase/client';

interface OrganisationDefaultUrls {
  organisation_id: string;
  default_ordering_url?: string;
  default_review_url?: string;
  default_instagram_url?: string;
  default_facebook_url?: string;
  default_google_review_url?: string;
  template_restaurant_name?: string;
  created_at?: string;
  updated_at?: string;
}

interface RestaurantUrlStatus {
  id: string;
  name: string;
  hasOrderingUrl: boolean;
  hasReviewUrl: boolean;
  hasInstagramUrl: boolean;
  hasFacebookUrl: boolean;
  hasGoogleReviewUrl: boolean;
}

const OrganisationURLSettings: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [urls, setUrls] = useState<OrganisationDefaultUrls>({
    organisation_id: ''
  });
  const [originalUrls, setOriginalUrls] = useState<OrganisationDefaultUrls>({
    organisation_id: ''
  });
  const [restaurantStatuses, setRestaurantStatuses] = useState<RestaurantUrlStatus[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Check if user has permission (org_admin or super_admin)
  // Check both role and jwt_role for compatibility
  const userRole = user?.jwt_role || user?.role;
  const hasPermission = hasOrgAdminAccess(userRole || '');
  
  // Get organisation ID - check both formats
  const organisationId = user?.organisation_id || user?.organisationId;

  useEffect(() => {
    if (hasPermission && organisationId) {
      setUrls(prev => ({ ...prev, organisation_id: organisationId }));
      setOriginalUrls(prev => ({ ...prev, organisation_id: organisationId }));
      fetchOrganisationUrls(organisationId);
      fetchRestaurantStatuses(organisationId);
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [organisationId, hasPermission, authLoading]);

  const fetchOrganisationUrls = async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from('organisation_default_urls')
        .select('*')
        .eq('organisation_id', orgId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setUrls(data);
        setOriginalUrls(data);
      }
    } catch (error) {
      console.error('Error fetching organisation URLs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organisation default URLs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantStatuses = async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from('restaurants_master')
        .select('id, restaurant_name, ordering_page_url, review_page_url, instagram_url, facebook_url, google_review_url')
        .eq('organisation_id', orgId);

      if (error) throw error;

      const statuses: RestaurantUrlStatus[] = (data || []).map(restaurant => ({
        id: restaurant.id,
        name: restaurant.restaurant_name || 'Unknown Restaurant',
        hasOrderingUrl: !!restaurant.ordering_page_url,
        hasReviewUrl: !!restaurant.review_page_url,
        hasInstagramUrl: !!restaurant.instagram_url,
        hasFacebookUrl: !!restaurant.facebook_url,
        hasGoogleReviewUrl: !!restaurant.google_review_url
      }));

      setRestaurantStatuses(statuses);
    } catch (error) {
      console.error('Error fetching restaurant statuses:', error);
    }
  };

  // Strip protocol (http:// or https://) and trailing slashes from URL for storage
  const stripProtocol = (url: string): string => {
    if (!url) return '';
    return url.replace(/^https?:\/\//i, '').replace(/\/+$/, '').trim();
  };

  // Validate URL - accepts with or without protocol
  const validateUrl = (url: string, fieldName: string): boolean => {
    if (!url) return true; // Empty is valid

    // Strip protocol for validation, then add https:// to test with URL constructor
    const stripped = stripProtocol(url);
    if (!stripped) return true;

    try {
      new URL(`https://${stripped}`);
      return true;
    } catch {
      setErrors(prev => ({ ...prev, [fieldName]: 'Please enter a valid URL' }));
      return false;
    }
  };

  const handleUrlChange = (field: keyof OrganisationDefaultUrls, value: string) => {
    setUrls(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleSave = async () => {
    // Validate all URLs
    const isValid =
      validateUrl(urls.default_ordering_url || '', 'default_ordering_url') &&
      validateUrl(urls.default_review_url || '', 'default_review_url') &&
      validateUrl(urls.default_instagram_url || '', 'default_instagram_url') &&
      validateUrl(urls.default_facebook_url || '', 'default_facebook_url') &&
      validateUrl(urls.default_google_review_url || '', 'default_google_review_url');

    if (!isValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the invalid URLs before saving',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      // Strip protocols before saving - URLs are stored without http:// or https://
      // default_google_review_url is intentionally saved as-is so short-link redirects keep working.
      const strippedUrls = {
        default_ordering_url: stripProtocol(urls.default_ordering_url || '') || null,
        default_review_url: stripProtocol(urls.default_review_url || '') || null,
        default_instagram_url: stripProtocol(urls.default_instagram_url || '') || null,
        default_facebook_url: stripProtocol(urls.default_facebook_url || '') || null,
        default_google_review_url: urls.default_google_review_url?.trim() || null,
      };

      const { error } = await supabase
        .from('organisation_default_urls')
        .upsert({
          organisation_id: organisationId,
          ...strippedUrls,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Update local state with stripped values
      const updatedUrls = { ...urls, ...strippedUrls };
      setUrls(updatedUrls);
      setOriginalUrls(updatedUrls);
      toast({
        title: 'Success',
        description: 'Organisation default URLs saved successfully'
      });
    } catch (error) {
      console.error('Error saving URLs:', error);
      toast({
        title: 'Error',
        description: 'Failed to save organisation default URLs',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(urls) !== JSON.stringify(originalUrls);

  const countMissingUrls = (status: RestaurantUrlStatus): number => {
    let count = 0;
    if (!status.hasOrderingUrl) count++;
    if (!status.hasReviewUrl) count++;
    if (!status.hasInstagramUrl) count++;
    if (!status.hasFacebookUrl) count++;
    if (!status.hasGoogleReviewUrl) count++;
    return count;
  };

  // Show auth loading state
  if (authLoading) {
    return <div>Loading authentication...</div>;
  }

  if (!user) {
    return <div>Loading user data...</div>;
  }

  if (!organisationId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No organisation ID found for this user. Please contact support.
        </AlertDescription>
      </Alert>
    );
  }

  if (!hasPermission) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You need to be an organization admin to access this page.
          <br />
          <span className="text-xs text-muted-foreground">
            Current role: {userRole || 'No role found'}
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Organisation Default URLs</CardTitle>
          <CardDescription>
            Set default URLs that will be used when a customer's preferred restaurant 
            doesn't have specific URLs configured. These act as fallbacks for dynamic 
            SMS variables.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Ordering URL */}
          <div className="space-y-2">
            <Label htmlFor="ordering_url" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Default Ordering Page URL
            </Label>
            <Input
              id="ordering_url"
              type="url"
              value={urls.default_ordering_url || ''}
              onChange={(e) => handleUrlChange('default_ordering_url', e.target.value)}
              placeholder="https://order.example.com"
              className={errors.default_ordering_url ? 'border-destructive' : ''}
            />
            {errors.default_ordering_url && (
              <p className="text-sm text-destructive">{errors.default_ordering_url}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Used for {'{preferred restaurant ordering url}'}
            </p>
          </div>

          {/* Review URL */}
          <div className="space-y-2">
            <Label htmlFor="review_url" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Default Review Page URL
            </Label>
            <Input
              id="review_url"
              type="url"
              value={urls.default_review_url || ''}
              onChange={(e) => handleUrlChange('default_review_url', e.target.value)}
              placeholder="https://review.example.com"
              className={errors.default_review_url ? 'border-destructive' : ''}
            />
            {errors.default_review_url && (
              <p className="text-sm text-destructive">{errors.default_review_url}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Used for {'{preferred restaurant review url}'}
            </p>
          </div>

          {/* Instagram URL */}
          <div className="space-y-2">
            <Label htmlFor="instagram_url" className="flex items-center gap-2">
              <Instagram className="h-4 w-4" />
              Default Instagram URL
            </Label>
            <Input
              id="instagram_url"
              type="url"
              value={urls.default_instagram_url || ''}
              onChange={(e) => handleUrlChange('default_instagram_url', e.target.value)}
              placeholder="https://instagram.com/yourbusiness"
              className={errors.default_instagram_url ? 'border-destructive' : ''}
            />
            {errors.default_instagram_url && (
              <p className="text-sm text-destructive">{errors.default_instagram_url}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Used for {'{preferred restaurant instagram url}'}
            </p>
          </div>

          {/* Facebook URL */}
          <div className="space-y-2">
            <Label htmlFor="facebook_url" className="flex items-center gap-2">
              <Facebook className="h-4 w-4" />
              Default Facebook URL
            </Label>
            <Input
              id="facebook_url"
              type="url"
              value={urls.default_facebook_url || ''}
              onChange={(e) => handleUrlChange('default_facebook_url', e.target.value)}
              placeholder="https://facebook.com/yourbusiness"
              className={errors.default_facebook_url ? 'border-destructive' : ''}
            />
            {errors.default_facebook_url && (
              <p className="text-sm text-destructive">{errors.default_facebook_url}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Used for {'{preferred restaurant facebook url}'}
            </p>
          </div>

          {/* Google Review URL */}
          <div className="space-y-2">
            <Label htmlFor="google_review_url" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Default Google Review URL
            </Label>
            <Input
              id="google_review_url"
              type="url"
              value={urls.default_google_review_url || ''}
              onChange={(e) => handleUrlChange('default_google_review_url', e.target.value)}
              placeholder="https://g.page/review/..."
              className={errors.default_google_review_url ? 'border-destructive' : ''}
            />
            {errors.default_google_review_url && (
              <p className="text-sm text-destructive">{errors.default_google_review_url}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Used for {'{preferred restaurant google review url}'}
            </p>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Restaurant URL Status */}
      <Card>
        <CardHeader>
          <CardTitle>Restaurant URL Coverage</CardTitle>
          <CardDescription>
            Overview of which restaurants have URLs configured. Missing URLs will 
            fall back to the organisation defaults above.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {restaurantStatuses.map(status => {
              const missingCount = countMissingUrls(status);
              return (
                <div key={status.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{status.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge 
                        variant={status.hasOrderingUrl ? "success" : "secondary"}
                        className="text-xs"
                      >
                        {status.hasOrderingUrl ? <CheckCircle className="h-3 w-3 mr-1" /> : null}
                        Ordering
                      </Badge>
                      <Badge 
                        variant={status.hasReviewUrl ? "success" : "secondary"}
                        className="text-xs"
                      >
                        {status.hasReviewUrl ? <CheckCircle className="h-3 w-3 mr-1" /> : null}
                        Review
                      </Badge>
                      <Badge 
                        variant={status.hasInstagramUrl ? "success" : "secondary"}
                        className="text-xs"
                      >
                        {status.hasInstagramUrl ? <CheckCircle className="h-3 w-3 mr-1" /> : null}
                        Instagram
                      </Badge>
                      <Badge 
                        variant={status.hasFacebookUrl ? "success" : "secondary"}
                        className="text-xs"
                      >
                        {status.hasFacebookUrl ? <CheckCircle className="h-3 w-3 mr-1" /> : null}
                        Facebook
                      </Badge>
                      <Badge 
                        variant={status.hasGoogleReviewUrl ? "success" : "secondary"}
                        className="text-xs"
                      >
                        {status.hasGoogleReviewUrl ? <CheckCircle className="h-3 w-3 mr-1" /> : null}
                        Google
                      </Badge>
                    </div>
                  </div>
                  {missingCount > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {missingCount} missing
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Help Information */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>How it works:</strong> When a dynamic variable like {'{preferred restaurant ordering url}'} is used in an SMS template, the system will:
          <ol className="list-decimal list-inside mt-2 space-y-1">
            <li>First try to use the URL from the customer's most recent restaurant</li>
            <li>If not available, use the organisation default URL configured above</li>
            <li>If still not available, use the URL from the restaurant that created the trigger</li>
          </ol>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default OrganisationURLSettings;